import { isNumber, isPlainObject, isString } from "lodash-es";
import { DateTime } from "luxon";

import type { MediaRecorderTranscriptTurn } from "@/store/audio/recording/media-types";
import { AssemblyAiTranscriberPayloadType } from "@/store/audio/recording/media-transcriber/transcriber.types";

type AssemblyAiTurnPayload = {
  transcript?: unknown;
  text?: unknown;
  words?: unknown;
  turn_order?: unknown;
  turnOrder?: unknown;
  timestamp?: unknown;
  audio_start?: unknown;
  audio_end?: unknown;
  channel?: unknown;
};

const getString = (value: unknown): string | undefined =>
  isString(value) && value.length ? value : undefined;

const getNumber = (value: unknown): number | undefined => {
  if (!isNumber(value) || Number.isNaN(value)) {
    return undefined;
  }
  return value;
};

export function resolveAssemblyAiRealtimePayloadType({ raw }: { raw: unknown }): string {
  if (!isPlainObject(raw)) return "unknown";
  const rawObject = raw as Record<string, unknown>;
  const candidate = (rawObject.type ?? rawObject.message_type) as unknown;

  switch (candidate) {
    case "Turn":
      return AssemblyAiTranscriberPayloadType.Turn;
    case "Begin":
      return AssemblyAiTranscriberPayloadType.Begin;
    case "Termination":
      return AssemblyAiTranscriberPayloadType.Termination;
    default:
      return AssemblyAiTranscriberPayloadType.Unknown;
  }
}

export function mapAssemblyAiTurnPayloadToRecorderTurn({
  raw,
}: {
  raw: unknown;
}): MediaRecorderTranscriptTurn | null {
  if (!isPlainObject(raw)) return null;
  const payload = raw as AssemblyAiTurnPayload;
  const transcript = getString(payload.transcript) ?? getString(payload.text);
  if (!transcript) return null;

  const channelNumber = getNumber(payload.channel);
  const channel: 0 | 1 = channelNumber === 1 ? 1 : 0;
  const audioStart = getNumber(payload.audio_start);
  const timestamp = getNumber(payload.timestamp);
  const timestampMs = normalizeAudioRecordingTimestamp({
    value: audioStart ?? timestamp,
  });
  const turnOrder = getNumber(payload.turn_order) ?? getNumber(payload.turnOrder) ?? 0;

  const wordsInput = Array.isArray(payload.words) ? payload.words : undefined;
  const words = wordsInput?.map(rawWord => {
    if (!isPlainObject(rawWord)) {
      return {
        word: "",
        startMs: timestampMs,
        endMs: timestampMs,
      };
    }

    const wordRecord = rawWord as Record<string, unknown>;
    const text = getString(wordRecord["text"]) ?? getString(wordRecord["word"]) ?? "";
    const start = getNumber(wordRecord["start"] ?? wordRecord["start_ms"]);
    const end = getNumber(wordRecord["end"] ?? wordRecord["end_ms"]);

    return {
      word: text,
      startMs: normalizeAudioRecordingTimestamp({
        value: start,
      }),
      endMs: normalizeAudioRecordingTimestamp({
        value: end,
      }),
    };
  });

  return {
    channel,
    transcript,
    timestampMs,
    words,
    raw,
    turnOrder,
  };
}

export function normalizeAudioRecordingTimestamp({ value }: { value: number | undefined }): number {
  // If the value is null or undefined, use the current timestamp.
  if (value == null || Number.isNaN(value)) {
    return DateTime.now().toMillis();
  }

  const rawMillis = value > 10_000 ? value : value * 1000;
  return DateTime.fromMillis(rawMillis).toMillis();
}

type CollapsibleTurn = {
  channel: number;
  content?: {
    words?: Array<{ text: string }>;
    turnTimestamp?: DateTime;
  };
  updatedAt?: DateTime;
};

const sanitizeTranscriptText = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/gi, "")
    .replace(/\s+/g, " ")
    .trim();

const getTurnText = (turn: { content?: { words?: Array<{ text: string }> } }): string =>
  (turn.content?.words ?? []).map(word => word.text ?? "").join(" ");

const getTurnTimestamp = (turn: {
  content?: { turnTimestamp?: DateTime };
  updatedAt?: DateTime;
}): number => {
  const timestamp = turn.content?.turnTimestamp?.toMillis?.();
  if (typeof timestamp === "number") {
    return timestamp;
  }
  const updatedAt = turn.updatedAt?.toMillis?.();
  return typeof updatedAt === "number" ? updatedAt : 0;
};

export function collapseRecorderTranscriptTurns<T extends CollapsibleTurn>(turns: T[]): T[] {
  if (!turns.length) return [];

  const sorted = [...turns].sort((a, b) => getTurnTimestamp(a) - getTurnTimestamp(b));
  const collapsed: T[] = [];

  sorted.forEach(turn => {
    const text = getTurnText(turn);
    const normalizedText = sanitizeTranscriptText(text);

    if (!normalizedText) {
      collapsed.push(turn);
      return;
    }

    const last = collapsed[collapsed.length - 1];

    if (last && last.channel === turn.channel) {
      const lastText = getTurnText(last);
      const lastNormalized = sanitizeTranscriptText(lastText);

      if (
        lastNormalized &&
        (normalizedText === lastNormalized ||
          normalizedText.startsWith(lastNormalized) ||
          lastNormalized.startsWith(normalizedText))
      ) {
        collapsed[collapsed.length - 1] = turn;
        return;
      }
    }

    collapsed.push(turn);
  });

  return collapsed;
}
